import { auth } from '../config/firebase';
import axios from 'axios';

const API_URL = import.meta.env.VITE_API_URL || 'http://localhost:3001/api';

async function authHeaders(): Promise<Record<string, string>> {
  const user = auth.currentUser;
  if (!user) return {};
  const token = await user.getIdToken();
  return { Authorization: `Bearer ${token}` };
}

export interface Expense {
  _id: string;
  description: string;
  category: string;
  vendor: string;
  amount: number;
  currency: string;
  fxRate: number;
  amountGBP: number;
  date: string;
  recurrence: 'once' | 'monthly' | 'yearly';
  notes: string;
  paidBy: string;
  // 'anthropic' on rows the daily cost sync maintains; the screen folds those into one line.
  source?: string;
  externalId?: string;
}

export interface ExpenseSummary {
  count: number;
  total: number;
  thisMonth: number;
  thisYear: number;
  monthlyRunRate: number;
  byCategory: { category: string; amount: number }[];
  knownCategories: string[];
  knownVendors: string[];
}

export async function listExpenses(): Promise<Expense[]> {
  const { data } = await axios.get(`${API_URL}/expenses`, { headers: await authHeaders() });
  return data;
}

export async function expenseSummary(): Promise<ExpenseSummary> {
  const { data } = await axios.get(`${API_URL}/expenses/summary`, { headers: await authHeaders() });
  return data;
}

export async function createExpense(body: Partial<Expense>): Promise<Expense> {
  const { data } = await axios.post(`${API_URL}/expenses`, body, { headers: await authHeaders() });
  return data;
}

export async function updateExpense(id: string, body: Partial<Expense>): Promise<Expense> {
  const { data } = await axios.patch(`${API_URL}/expenses/${id}`, body, { headers: await authHeaders() });
  return data;
}

export async function deleteExpense(id: string): Promise<void> {
  await axios.delete(`${API_URL}/expenses/${id}`, { headers: await authHeaders() });
}

export interface AnthropicSyncResult {
  from: string; to: string; days: number; created: number; updated: number; totalUsd: number;
}

// Pull Anthropic API spend from their cost report into the list (one row per day). `from` backfills.
export async function syncAnthropicExpenses(from?: string): Promise<AnthropicSyncResult> {
  const { data } = await axios.post(`${API_URL}/expenses/sync-anthropic`, from ? { from } : {}, { headers: await authHeaders() });
  return data;
}
